import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  RestoreBackupDTO,
  PagingOptions,
  QueryResultDTO,
  CreateInstantBackupDTO,
  BackupId,
  BackupDTO,
  WebsiteBackupSummaryDTO,
  WebsiteBackupId,
  WebsiteBackupConfigDTO,
  DatabaseBackupSummaryDTO,
  DatabaseBackupId,
  DatabaseBackupConfigDTO,
  Backup,
  WebsiteBackup,
  RequestRestoreDto,
} from '../../shared/models/backups.models';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class BackupsService {
  private http = inject(HttpClient);

  baseURL = '/backups/backups';

  restoreBackup(data: RestoreBackupDTO): Observable<void> {
    return this.http.post<void>('/backups/restores', data);
  }

  restoreWebsiteBackups(data: RequestRestoreDto): Observable<void> {
    return this.http.post<void>('/backups/tasks/restores', data);
  }

  searchBackups(query: PagingOptions): Observable<QueryResultDTO<Backup>> {
    const params = new HttpParams().set('page', query.page).set('pageSize', query.pageSize);

    if (query.websiteId) params.set('websiteId', query.websiteId);

    return this.http
      .get<[Backup[], number]>(this.baseURL, { params })
      .pipe(map(([data, total]) => ({ data, total })));
  }

  searchWebsiteBackups(query: PagingOptions): Observable<QueryResultDTO<WebsiteBackup>> {
    let params = new HttpParams()
      .set('websiteId', query.websiteId!)
      .set('page', query.page)
      .set('pageSize', query.pageSize);

    if (query.timestampFrom) params = params.set('timestampFrom', query.timestampFrom);
    if (query.timestampTo) params = params.set('timestampTo', query.timestampTo);

    return this.http
      .get<[WebsiteBackup[], number]>(`${this.baseURL}/websites/search`, { params })
      .pipe(map(([data, total]) => ({ data, total })));
  }

  createBackup(backup: CreateInstantBackupDTO): Observable<void> {
    return this.http.post<void>(this.baseURL, backup);
  }

  getBackup(id: BackupId): Observable<BackupDTO> {
    return this.http.get<BackupDTO>(`${this.baseURL}/${id}`);
  }

  deleteBackup(id: BackupId): Observable<void> {
    return this.http.delete<void>(`${this.baseURL}/${id}`);
  }

  getWebsiteBackups(
    id: BackupId,
    options: PagingOptions,
  ): Observable<QueryResultDTO<WebsiteBackupSummaryDTO>> {
    const params = new HttpParams().set('page', options.page).set('pageSize', options.pageSize);

    return this.http
      .get<[WebsiteBackupSummaryDTO[], number]>(`${this.baseURL}/${id}/website`, {
        params,
      })
      .pipe(map(([data, total]) => ({ data, total })));
  }

  getWebsiteBackupConfig(id: WebsiteBackupId): Observable<WebsiteBackupConfigDTO> {
    return this.http.get<WebsiteBackupConfigDTO>(`${this.baseURL}/website-backups/${id}/config`);
  }

  getDatabaseBackups(
    id: BackupId,
    options: PagingOptions,
  ): Observable<QueryResultDTO<DatabaseBackupSummaryDTO>> {
    const params = new HttpParams().set('page', options.page).set('pageSize', options.pageSize);

    return this.http
      .get<[DatabaseBackupSummaryDTO[], number]>(`${this.baseURL}/${id}/database`, {
        params,
      })
      .pipe(map(([data, total]) => ({ data, total })));
  }

  getDatabaseBackupConfig(id: DatabaseBackupId): Observable<DatabaseBackupConfigDTO> {
    return this.http.get<DatabaseBackupConfigDTO>(`${this.baseURL}/database-backups/${id}/config`);
  }
}
